import Fade from '@mui/material/Fade'
import { MainApi } from 'api/MainApi'
import { StyledDropdown } from 'components/Dropdown/StyledDropdown'
import Loader from 'components/Loader/Loader'
import { ReactComponent as DropdownIcon } from 'images/icons/arrow_select.svg'
import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PROGRESS_TYPES } from 'utils/constants'

import style from './RecordedRouteSelect.module.css'

// ... imports are fine ...

interface RecordedRouteSelectProps {
  onSelect: (name: string, id: number) => void
  placeholder?: string
  value?: string
  description?: string
}

const FileItem = ({ file, onClick }: { file: any; onClick: () => void }) => {
  let formattedDate = 'Unknown date'
  try {
    const timestamp = Math.floor(file.modified * 1000)
    if (!isNaN(timestamp) && timestamp > 0) {
      formattedDate = new Date(timestamp)
        .toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
        .replace(',', '')
    }
  } catch (e) {
    console.warn('Invalid date for file:', file.name, e)
  }

  const displayName = file.name ? file.name.replace(/\.[^/.]+$/, '') : 'Unnamed file'

  return (
    <div className={style.dropdownItem} onClick={onClick}>
      <span>{displayName}</span>
      <span>{formattedDate}</span>
    </div>
  )
}

const PAGE_SIZE = 20

const RecordedRouteSelect = ({ onSelect, placeholder, value, description }: RecordedRouteSelectProps) => {
  const { t } = useTranslation()
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [files, setFiles] = useState<any[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const currentFetchIdRef = useRef(0)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const id = useId()

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const fetchData = async (pageNum: number, search: string, isAppend = false) => {
    currentFetchIdRef.current += 1
    const fetchId = currentFetchIdRef.current

    try {
      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.WORK)
      } else {
        setIsFetchingMore(true)
      }
      const response = await MainApi.fetchRecordedRoutes(pageNum, PAGE_SIZE, search || undefined)

      if (fetchId !== currentFetchIdRef.current) return

      const mappedFiles = response.results.map(route => ({
        name: route.properties.name || route.properties.filename || '',
        id: route.id,
        size: 0,
        modified: route.properties.recordedAt ? new Date(route.properties.recordedAt).getTime() / 1000 : 0,
      }))

      if (isAppend) {
        setFiles(prev => [...prev, ...mappedFiles])
      } else {
        setFiles(mappedFiles)
      }

      setHasMore(pageNum * PAGE_SIZE < response.count)
      setPage(pageNum)

      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.SUCCESS)
      }
    } catch (error) {
      if (fetchId !== currentFetchIdRef.current) return
      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.ERROR)
      }
      console.error('Error fetching files:', error)
    } finally {
      if (fetchId === currentFetchIdRef.current) {
        setIsFetchingMore(false)
      }
    }
  }

  useEffect(() => {
    if (open) {
      if (files.length === 0 || debouncedSearchQuery !== '') {
        fetchData(1, debouncedSearchQuery, false)
      }
      // Focus input when opened
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedSearchQuery])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!open) return
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (loadingProgress === PROGRESS_TYPES.SUCCESS && hasMore && !isFetchingMore) {
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        fetchData(page + 1, debouncedSearchQuery, true)
      }
    }
  }

  const handleFileSelect = (file: any) => {
    onSelect(file.name, file.id)
    handleClose()
  }

  const handleClickTrigger = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setTimeout(() => {
      setPage(1)
      setHasMore(true)
      setIsFetchingMore(false)
      setSearchQuery('')
      setDebouncedSearchQuery('')
      setFiles([])
    }, 300)
  }

  const dropdownWidth = anchorEl?.clientWidth

  return (
    <div className={style.container}>
      {description && <div className={style.subtitle}>{description}</div>}
      <div className={style.trigger} onClick={handleClickTrigger}>
        <div className={`${style.triggerText} ${!value ? style.placeholderColor : ''}`}>
          {value || placeholder || t('zone_details.select_recorded_route')}
        </div>
        <div className={`${style.triggerIcon} ${open ? style.triggerIconOpen : ''}`}>
          <DropdownIcon />
        </div>
      </div>

      {/* ... rest of the component ... */}

      <StyledDropdown
        MenuListProps={{ 'aria-labelledby': id }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        container={typeof document !== 'undefined' ? () => document.body : undefined}
        disablePortal={false}
        disableEnforceFocus={true}
        disableRestoreFocus={true}
        PaperProps={{
          style: dropdownWidth ? { width: dropdownWidth, maxHeight: 400 } : undefined,
        }}
      >
        <div className={style.searchContainer} onClick={e => e.stopPropagation()}>
          <input
            ref={inputRef}
            className={style.searchInput}
            type="text"
            placeholder={t('common.search') + '...'}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.stopPropagation()}
          />
        </div>

        <div className={style.listContainer} onScroll={handleScroll}>
          {loadingProgress === PROGRESS_TYPES.WORK && (
            <div className={style.loading}>
              <Loader />
            </div>
          )}

          <div className={style.tableHeader}>
            <span>Name</span>
            <span>Created at</span>
          </div>

          {files.map((file, index) => (
            <FileItem key={`${file.id || file.name}-${index}`} file={file} onClick={() => handleFileSelect(file)} />
          ))}

          {loadingProgress === PROGRESS_TYPES.SUCCESS && files.length === 0 && (
            <div className={style.loading}>No items found.</div>
          )}

          {loadingProgress === PROGRESS_TYPES.ERROR && <div className={style.loading}>Error loading routes</div>}

          {isFetchingMore && (
            <div className={style.loading}>
              <Loader />
            </div>
          )}
        </div>
      </StyledDropdown>
    </div>
  )
}

export default RecordedRouteSelect
